/** @jsx jsx */
import React, { useEffect, useState } from 'react';
import { css, styled, jsx } from '@compiled/react';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { N100 } from '@atlaskit/theme/colors';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { useTrialBannerExperiment } from '../controllers';
import { messages } from './messages';
import { FreeToPremiumModal } from './modal';

const background = `linear-gradient(225deg, ${token(
	'color.background.accent.purple.subtlest',
	'#F3F0FF',
)} 0%, ${token('color.background.accent.blue.subtlest', '#E9F2FF')} 51.89%, ${token(
	'color.background.accent.teal.subtlest',
	'#E3FAFC',
)} 100%)`;

const BANNER_CLOSE_BUTTON_TEST_ID = 'business-trial-banner.ui.close-button';

export interface TrialBannerProps {
	isProjectSimplified: boolean;
}

export const TrialBanner = ({ isProjectSimplified }: TrialBannerProps) => {
	const { formatMessage } = useIntl();
	const [bannerClosed, setBannerClosed] = useState(false);
	const [modalClosed, setModalClosed] = useState(true);
	const [billingSystem, setBillingSystem] = useState<string | null>(null);

	const { shouldRenderExperiment, clearCache } = useTrialBannerExperiment();
	const tenantContext = useTenantContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		setBillingSystem(
			tenantContext.productEntitlementDetails?.['jira-software']?.billingSourceSystem === 'CCP'
				? 'CCP'
				: 'HAMS',
		);
	}, [tenantContext]);

	const onClose = () => {
		fireUIAnalytics(
			createAnalyticsEvent({ action: 'clicked', actionSubject: 'button' }),
			'jwmMonetisationModalDismiss',
			{
				projectType: isProjectSimplified ? 'TMP' : 'CMP',
				billingType: billingSystem,
			},
		);
		setModalClosed(true);
	};

	const handleBannerClick = (
		_e: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		setModalClosed(false);
		fireUIAnalytics(analyticsEvent, 'jwmMonetisationTryPremium', {
			projectType: isProjectSimplified ? 'TMP' : 'CMP',
			billingSystem,
		});
	};

	const handleDismissClick = (
		_e: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticsEvent, 'jwmMonetisationBannerDismiss', {
			projectType: isProjectSimplified ? 'TMP' : 'CMP',
			billingSystem,
		});
		setBannerClosed(true);
		if (fg('jwm_create_project_from_sample')) {
			clearCache();
		}
	};

	return shouldRenderExperiment && !bannerClosed && billingSystem ? (
		<Container>
			<Box
				testId="business-trial-banner.ui.premium-upgrade"
				padding="space.300"
				xcss={premiumUpgradeStyles}
			>
				<Stack space="space.200" alignInline="start">
					<Inline space="space.100" spread="space-between" grow="fill" alignBlock="start">
						<Content>{formatMessage(messages.premiumTrialContentComplexspork)}</Content>
						<Button
							aria-label={formatMessage(messages.closeBannerAriaLabel)}
							appearance="subtle-link"
							css={styles}
							onClick={handleDismissClick}
							data-testId={BANNER_CLOSE_BUTTON_TEST_ID}
							iconBefore={
								<CrossIcon
									label=""
									size="small"
									// @ts-expect-error Property 'css' does not exist on type 'IntrinsicAttributes & IntrinsicClassAttributes<T> & Readonly<GlyphProps>'.ts(2769)
									css={styles2}
								/>
							}
						/>
					</Inline>
					<Inline space="space.100" grow="fill" alignBlock="end" alignInline="end">
						<Button
							onClick={handleBannerClick}
							appearance="primary"
							iconBefore={<PremiumIcon size="small" label="" />}
						>
							{formatMessage(messages.premiumTrialCta)}
						</Button>
					</Inline>
				</Stack>
			</Box>
			{!modalClosed && (
				<FreeToPremiumModal
					onClose={onClose}
					billingSystem={billingSystem}
					isProjectSimplified={isProjectSimplified}
				/>
			)}
		</Container>
	) : null;
};

export default TrialBanner;

const styles2 = css({ margin: 0 });

const styles = css({ padding: '0px', height: '16px', width: '16px' });

const premiumUpgradeStyles = xcss({
	background,
	width: '100%',
	paddingRight: 'space.200',
	paddingTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
	color: token('color.text', N100),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	position: 'absolute',
	bottom: 0,
});
